import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import authApiClient from '../authApiClient';
import { useUser } from './userContext';

const RecommendationContext = createContext();

export const useRecommendation = () => useContext(RecommendationContext);

export const RecommendationProvider = ({ children, initialLastSearch = '' }) => {
    const { user } = useUser();
    const isMounted = useRef(true);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Prefer an explicit seed from a Journey Guide mission transfer; otherwise
    // fall back to the user's last persisted search so the page remembers state
    // across visits.
    const [lastSearch, setLastSearch] = useState(() => {
        const seeded = String(initialLastSearch || '').trim();
        if (seeded) return seeded;
        if (!user) return '';
        const saved = localStorage.getItem(`lastRecommendationSearch_${user._id}`);
        return saved || '';
    });

    // Update localStorage whenever lastSearch changes
    useEffect(() => {
        if (user) {
            localStorage.setItem(`lastRecommendationSearch_${user._id}`, lastSearch);
        }
    }, [lastSearch, user]);

    const [carouselCategories, setCarouselCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [userSearches, setUserSearches] = useState([]);
    const [allSearchesCategories, setAllSearchesCategories] = useState([]);
    const [saving, setSaving] = useState(false);

    const fetchCarouselCategories = useCallback(async () => {
        if (!lastSearch || !lastSearch.trim()) {
            console.log('No valid search term provided.');
            return;
        }

        if (!isMounted.current) return;
        setIsLoading(true);
        setError(null);

        const isAllSearches = lastSearch === 'All Searches';
        const encodedInput = encodeURIComponent(lastSearch.trim().toLowerCase());
        const url = isAllSearches
            ? '/recommendations_all_searches'
            : `/recommendations/carousel-categories?userInput=${encodedInput}`;

        console.log(`Fetching categories from URL: ${url}`);

        try {
            const response = await authApiClient.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache',
                },
            });

            if (!isMounted.current) return;

            const data = response.data;
            console.log('Fetched data:', data);

            let categories = isAllSearches ? data.allSearchesCategories : data.carouselCategories || [];

            // Update the carousel categories state only if component is still mounted
            if (isMounted.current) {
                setCarouselCategories(categories);
            }

        } catch (error) {
            if (isMounted.current) {
                console.error('HTTP error!', error);
                setError('Failed to fetch categories');
                setCarouselCategories([]);
            }
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    }, [lastSearch]);

    const refreshCategories = useCallback(() => {
        if (isMounted.current) {
            fetchCarouselCategories();
        }
    }, [fetchCarouselCategories]);

    const refreshUserSearches = useCallback(async () => {
        try {
            const response = await authApiClient.get('/recommendations/past-searches');
            if (isMounted.current) {
                setUserSearches(response.data.searchHistory || []);
                console.log('[RecommendationContext] User searches refreshed:', response.data.searchHistory);
            }
        } catch (error) {
            if (isMounted.current) {
                console.error('[RecommendationContext] Error fetching past searches:', error);
            }
        }
    }, []);

    return (
        <RecommendationContext.Provider
            value={{
                lastSearch,
                setLastSearch,
                carouselCategories,
                setCarouselCategories,
                isLoading,
                setIsLoading,
                error,
                setError,
                userSearches,
                setUserSearches,
                allSearchesCategories,
                setAllSearchesCategories,
                refreshCategories,
                fetchCarouselCategories,
                refreshUserSearches,
                saving,
                setSaving,
            }}
        >
            {children}
        </RecommendationContext.Provider>
    );
};
